'use client'

import { useState } from 'react'

import { cn } from '@/utilities/ui'
import type { AcademyLessonSummary } from '@/utilities/academyCourseMeta'
import { LessonContentPanel } from './LessonContentPanel'

interface Props {
  lessons: AcademyLessonSummary[]
  courseSlug: string
  difficulty?: string | null
  /** Per-course illustration slug. */
  illustration?: string | null
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 8 8" fill="none" aria-hidden="true" className={className}>
      <path d="M4 8L3.2875 7.3L6.0875 4.5H0V3.5H6.0875L3.2875 0.7L4 0L8 4L4 8Z" fill="currentColor" />
    </svg>
  )
}

/**
 * Interactive lesson timeline. A single `activeIndex` drives both layouts:
 *  - Desktop (lg+): a 280px clickable rail of lesson rows swaps the content panel.
 *  - Tablet/mobile: a prev/next pager over a full-width panel.
 *
 * A plain useState index fits this swap-by-selection UI better than
 * useCarouselScroll (which is built for horizontally-scrolling card carousels).
 */
export function CourseTimeline({ lessons, courseSlug, difficulty, illustration }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)

  if (lessons.length === 0) return null

  const lastIndex = lessons.length - 1
  const active = lessons[activeIndex]
  const total = String(lessons.length).padStart(2, '0')
  const stepperLabel = `Lesson ${String(activeIndex + 1).padStart(2, '0')} of ${total}`

  // Shared between the tablet and mobile stepper layouts.
  const arrows = (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
        disabled={activeIndex === 0}
        aria-label="Previous lesson"
        className="flex size-10 items-center justify-center rounded-full border border-black/20 transition-colors hover:bg-civo-cloud/60 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ArrowIcon className="size-3 rotate-180" />
      </button>
      <button
        type="button"
        onClick={() => setActiveIndex((i) => Math.min(lastIndex, i + 1))}
        disabled={activeIndex === lastIndex}
        aria-label="Next lesson"
        className="flex size-10 items-center justify-center rounded-full border border-black/20 transition-colors hover:bg-civo-cloud/60 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ArrowIcon className="size-3" />
      </button>
    </div>
  )

  const progress = (
    <div className="flex w-full gap-1.5" aria-hidden="true">
      {lessons.map((lesson, index) => (
        <span
          key={lesson.id}
          className={cn(
            'h-1 flex-1 rounded-[2px]',
            index <= activeIndex ? 'bg-black' : 'bg-civo-cloud',
          )}
        />
      ))}
    </div>
  )

  return (
    <div>
      {/* Announces the swapped lesson to screen readers (rail/pager change the panel in place). */}
      <p className="sr-only" role="status">
        {`Showing lesson ${activeIndex + 1} of ${lessons.length}: ${active.title}`}
      </p>

      {/* Desktop: rail + panel. items-start keeps the panel at its content height
          instead of stretching to match the (often taller) lesson rail. */}
      <div className="hidden lg:flex lg:items-start lg:gap-14">
        <div className="w-[280px] shrink-0">
          <ul className="flex flex-col gap-1">
            {lessons.map((lesson, index) => {
              const isActive = index === activeIndex
              return (
                <li key={lesson.id} className="relative">
                  {/* Connector down to the next dot; omitted on the last row so the line ends there. */}
                  {index < lastIndex && (
                    <span
                      aria-hidden="true"
                      className="absolute -bottom-7 left-[19px] top-6 w-0.5 bg-stone-300"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-current={isActive ? 'true' : undefined}
                    className={cn(
                      'relative z-10 flex w-full items-start gap-4 rounded-[12px] py-3.5 pl-3 pr-4 text-left transition-colors',
                      isActive ? 'bg-civo-cloud' : 'hover:bg-civo-cloud/40',
                    )}
                  >
                    <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
                      <span
                        className={cn(
                          'size-4 rounded-full',
                          isActive ? 'bg-black' : 'border-2 border-stone-300 bg-white',
                        )}
                      />
                    </span>
                    <span className="flex min-w-0 flex-col gap-2">
                      <span className="flex flex-col gap-1">
                        <span className="body-xsmall text-stone-600">
                          Lesson {String(index + 1).padStart(2, '0')}
                        </span>
                        {/* Constant size across states so the rail doesn't reflow on click;
                            the active row is distinguished by its cloud background + filled dot. */}
                        <span className="subheading-xsmall text-black">{lesson.title}</span>
                      </span>
                      {lesson.readingTimeMinutes ? (
                        <span className="body-small text-stone-600">
                          {lesson.readingTimeMinutes} min
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        <LessonContentPanel
          lesson={active}
          index={activeIndex}
          courseSlug={courseSlug}
          difficulty={difficulty}
          illustration={illustration}
          className="lg:flex-1"
        />
      </div>

      {/* Tablet/mobile: lesson stepper (Figma 1181:3058) + panel.
          Stacked layout used across the whole sub-desktop range. */}
      <div className="lg:hidden">
        <div className="flex flex-col gap-4 rounded-[8px] border border-civo-cloud bg-white p-5">
          <span className="text-[13px] leading-normal text-stone-600">{stepperLabel}</span>
          <span className="subheading-medium text-black">{active.title}</span>
          {progress}
          <div className="flex justify-end">{arrows}</div>
        </div>

        <LessonContentPanel
          lesson={active}
          index={activeIndex}
          courseSlug={courseSlug}
          difficulty={difficulty}
          illustration={illustration}
          className="mt-6"
        />
      </div>
    </div>
  )
}
